import React, { useState } from 'react';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  ButtonsWrapper,
  Button,
  Typography,
  TYPOGRAPHY_SIZE,
  ICONS,
  PLATFORM_THEME_COLORS,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';

import { useUpdateProcess } from 'src/hooks/processes/update-process.hook';

import { DrawerComponent } from 'src/components/custom-drawer';

import { ProcessTasksTable } from 'src/components/process-tasks-table';
import { ChangeProcessTask } from './change-process-task';

import { PROCESS_TASK_MODALS } from './edit-process-form.constants';

import { IProcessEditItem, IProcessTableTask } from './edit-process-form.interfaces';

import { EditProcessFormWrapper } from './edit-process-form.styles';



const EditProcessForm = ({
  process,
  tasks,
  onCancel,
}: {
  process?: IProcessEditItem;
  tasks?: IProcessTableTask[];
  onCancel: () => void;
}) => {
  const { t } = useTranslation();

  const [taskList, setTaskList] = useState<IProcessTableTask[]>(tasks || []);
  const [selectedTask, setSelectedTask] = useState<IProcessTableTask | null>(null);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [taskIdsToDelete, setTaskIdsToDelete] = useState<string[]>([]);

  const { _id, title } = process || {};

  const closeModal = () => {
    setActiveModal(null);
    setSelectedTask(null);
  };

  const onCreateTask = async (task: IProcessTableTask) => {
    setTaskList(prev => [{ key: uuidv4(), ...task, dueTo: moment(task.dueTo) }, ...prev]);
    closeModal();
  };

  const onUpdateTask = (task: IProcessTableTask) => {
    const editedTaskIndex = taskList.findIndex(el => el?.key === selectedTask?.key);
    const editedTask = taskList[editedTaskIndex];
    const newList = [...taskList];
    newList.splice(editedTaskIndex, 1, {
      ...editedTask,
      status: task.status,
      description: task?.description,
      checklist: task?.checklist,
    });
    setTaskList(newList);
    closeModal();
  };

  const handleTaskModalOpen = (type: string) => () => {
    setActiveModal(type);
  };

  const deleteTask = () => {
    setSelectedTask(null);
    closeModal();
  };

  const onSubmitDelete = () => {
    if (selectedTask?._id) {
      const newIds = [...taskIdsToDelete, selectedTask._id];
      setTaskIdsToDelete(newIds);
    }
    const newList = taskList.filter(task => task?.key !== selectedTask?.key);
    setTaskList(newList);
    deleteTask();
  };

  const isCreateTask = activeModal === PROCESS_TASK_MODALS.create;
  const isEditTask = activeModal === PROCESS_TASK_MODALS.edit;


  const { loading, updateProcess } = useUpdateProcess({ onSuccess: onCancel });

  const onSubmit = () => {
    updateProcess({
      variables: {
        processId: _id,
        payload: {
          taskIdsToDelete,
          processTasks: taskList.map(({ key, dueTo, ...rest }) => ({
            ...rest,
            dueTo: moment(dueTo).toDate(),
          })),
        },
      },
    });
  };

  return (
    <EditProcessFormWrapper>
      <DrawerComponent
        title={t(`processBuilder:tasks.labels.${isCreateTask ? 'addProcessTask' : 'editProcessTask'}`)}
        onClose={closeModal}
        visible={isEditTask || isCreateTask}
        destroyOnClose
      >
        <ChangeProcessTask
          onClose={closeModal}
          task={selectedTask}
          onCreateTask={onCreateTask}
          onUpdateTask={onUpdateTask}
        />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === PROCESS_TASK_MODALS.delete}
        title={t('processBuilder:tasks.deleteModal.title')}
        subTitle={t('processBuilder:tasks.deleteModal.subtitle', {
          taskName: selectedTask?.title,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {`${t('employee:process.labels.processes')}: ${title}`}
        </Typography>

        <ButtonsWrapper>
          <Button
            type="primary"
            ghost
            icon={ICONS.plus}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={handleTaskModalOpen(PROCESS_TASK_MODALS.create)}
          >
            {t('settings:processTemplates.buttons.addTask')}
          </Button>
          <Button type="primary" disabled={loading} onClick={onSubmit}>
            {t('common:buttons.save')}
          </Button>
        </ButtonsWrapper>
      </div>
      <ProcessTasksTable
        taskList={taskList}
        setTaskList={setTaskList}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setSelectedTask(record);
        }}
      />
    </EditProcessFormWrapper>
  );
};

export default EditProcessForm;
